import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { ArticleBlogStatusEnum } from '../../__generated__/graphql-global-types';
import { GET_BLOG_ARTICLES } from './query';

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  font-family: var(--font-primary), sans-serif;
`;

const PageHeader = styled.div``;

const PageTitle = styled.h1`
  font-family: 'font-semiBold';
  font-weight: 600;
  text-transform: uppercase;
  font-size: 4rem;
  text-align: center;
  color: var(--design-text-color);
  margin: 1.6rem 0;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem;
  padding: 2.4rem 0;
  /* border-bottom: 1px solid #e0e0e0; */
`;

const FilterButton = styled.button<{ $active: boolean }>`
  font-family: 'MontserratSemiBold', sans-serif;
  font-size: 1.1rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'var(--design-base-color)' : 'var(--design-text-color)'};
  background: ${({ $active }) =>
    $active ? 'var(--design-base-color)' : 'transparent'};
  color: ${({ $active }) => ($active ? '#fff' : 'var(--design-text-color)')};
  padding: 0.6rem 1.6rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ $active }) =>
      $active ? 'var(--design-base-color)' : 'var(--design-base-color)'};
    color: ${({ $active }) => ($active ? '#fff' : 'var(--design-base-color)')};
    background: ${({ $active }) =>
      $active ? 'var(--design-base-color)' : 'transparent'};
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 6rem 0;
`;

const Spinner = styled.div`
  width: 3.2rem;
  height: 3.2rem;
  border: 2px solid var(--design-base-color);
  border-top-color: var(--design-base-color);
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Card = styled.a`
  display: grid;
  grid-template-columns: 55% 1fr;
  gap: 4rem;
  text-decoration: none;
  color: inherit;
  padding: 4rem 0;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;

  @media screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  /* height: 390px; */
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  /* transition: transform 0.5s ease; */
`;

const ImageTitle = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2.4rem 2rem 1.6rem;
  font-family: var(--font-primary), sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, transparent 100%);
  transition: opacity 0.3s ease;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const CardTitle = styled.h2`
  font-family: var(--font-primary), 'Inter', serif;
  font-size: 2.4rem;
  font-weight: 500;
  color: var(--design-text-color);
  margin: 0;
`;

const Excerpt = styled.p`
  font-family: var(--font-primary), 'Inter', serif;
  font-weight: 400;
  font-size: 1.4rem;
  color: var(--design-text-color);
  line-height: 1.7;
  margin: 0;
`;

const Meta = styled.div`
  font-family: var(--font-primary), 'Inter', serif;
  font-weight: 300;
  display: flex;
  gap: 1.6rem;
  font-size: 1.2rem;
  color: #aaa;
`;

const Themes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Theme = styled.span`
  font-family: var(--font-primary), 'MontserratSemiBold', sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--design-base-color);
`;

const ReadMore = styled.span`
  display: inline-block;
  font-size: 1.2rem;
  font-family: var(--font-primary), 'MontserratMedium', sans-serif;
  font-weight: 500;
  color: var(--design-text-color);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-decoration: underline;
  padding-bottom: 1px;
  margin-top: 0.8rem;
`;

export const MagazineIndex = () => {
  const { t, i18n } = useTranslation('magazine', { useSuspense: false });
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [visibleArticles, setVisibleArticles] = useState<any[]>([]);
  const params = {
    dimensions: {
      width: 698,
      height: 390,
    },
  };

  const paramsBannerUrl = JSON.stringify(params);
  const withImageParams = (url: string) => `${url}?params=${paramsBannerUrl}`;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const { data } = useQuery(GET_BLOG_ARTICLES, {
    variables: {
      status: ArticleBlogStatusEnum.ALL_ARTICLES,
      pagination: { first: 200 },
    },
  });

  const articles = data?.blogArticles?.edges ?? [];

  const allThemes: string[] = Array.from(
    new Set(
      articles.flatMap(({ node }: { node: any }) =>
        (node.theme ?? []).map((t: { name: string }) => t.name)
      )
    )
  );

  useEffect(() => {
    if (articles.length === 0) return;
    setLoading(true);
    const timeout = setTimeout(() => {
      setVisibleArticles(
        activeFilter
          ? articles.filter(({ node }: { node: any }) =>
              node.theme?.some((t: { name: string }) => t.name === activeFilter)
            )
          : articles
      );
      setLoading(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [activeFilter, articles.length]);

  const handleFilter = (theme: string | null) => {
    if (theme === activeFilter) return;
    setActiveFilter(theme);
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle className="fontOldStandard">{t('title')}</PageTitle>
      </PageHeader>
      {allThemes.length > 0 && (
        <FilterBar>
          <FilterButton
            $active={activeFilter === null}
            onClick={() => handleFilter(null)}
          >
            {t('all')}
          </FilterButton>
          {allThemes.map((theme) => (
            <FilterButton
              key={theme}
              $active={activeFilter === theme}
              onClick={() => handleFilter(theme)}
            >
              {theme}
            </FilterButton>
          ))}
        </FilterBar>
      )}

      {loading ? (
        <LoaderWrapper>
          <Spinner />
        </LoaderWrapper>
      ) : (
        <List>
          {visibleArticles.map(({ node }: { node: any }) => (
            <Card
              key={node.id}
              {...(node.url ? { href: node.url } : { as: 'div' })}
            >
              <ImageWrapper>
                <CardImage src={withImageParams(node.image)} alt={node.title} />
                <ImageTitle className="font-semiBold">{node.title}</ImageTitle>
              </ImageWrapper>
              <Info>
                {node.theme?.length > 0 && (
                  <Themes>
                    {node.theme.map((theme: { name: string }) => (
                      <Theme className="font-semiBold" key={theme.name}>
                        {theme.name}
                      </Theme>
                    ))}
                  </Themes>
                )}
                <CardTitle className="font-medium">{node.title}</CardTitle>
                <Excerpt
                  className="font-regular"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      const plain = node.text?.replace(/<[^>]*>/g, '') ?? '';
                      return plain.length > 150
                        ? plain.slice(0, 150) + '…'
                        : plain;
                    })(),
                  }}
                />
                <Meta>
                  <span className="font-regular">
                    {formatDate(node.publicationDate)}
                  </span>
                </Meta>
                <ReadMore className="font-medium">{t('readArticle')}</ReadMore>
              </Info>
            </Card>
          ))}
        </List>
      )}
    </Container>
  );
};

export default MagazineIndex;
